import { useEffect, useMemo, useState } from 'react';
import ErrorBoundary from '../components/ErrorBoundary';
import ProductCard from '../components/ProductCard';
import { getProducts } from '../lib/products';

const PAGE_SIZE = 12;

function unique(values) {
  return Array.from(new Set(values)).sort((a, b) => a.localeCompare(b));
}

export default function ProductsPage() {
  const products = useMemo(() => getProducts(), []);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const [condition, setCondition] = useState('');
  const [routine, setRoutine] = useState('');
  const [priceRange, setPriceRange] = useState('');
  const [sort, setSort] = useState('name-asc');
  const [page, setPage] = useState(1);

  useEffect(() => {
    document.title = 'Products | Nyafaz Skincare';
  }, []);

  const categories = unique(products.map((product) => product.category));
  const conditions = unique(products.flatMap((product) => product.conditions));
  const routines = unique(products.flatMap((product) => product.routines));

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    const [minPrice, maxPrice] = priceRange ? priceRange.split('-').map(Number) : [0, Infinity];

    return products
      .filter((product) => {
        const haystack = [product.name, product.category, product.ingredients.join(' '), product.conditions.join(' ')]
          .join(' ')
          .toLowerCase();

        return (
          (!term || haystack.includes(term)) &&
          (!category || product.category === category) &&
          (!condition || product.conditions.includes(condition)) &&
          (!routine || product.routines.includes(routine)) &&
          product.price >= minPrice &&
          product.price <= maxPrice
        );
      })
      .sort((a, b) => {
        if (sort === 'price-asc') return a.price - b.price;
        if (sort === 'price-desc') return b.price - a.price;
        if (sort === 'name-desc') return b.name.localeCompare(a.name);
        return a.name.localeCompare(b.name);
      });
  }, [category, condition, priceRange, products, query, routine, sort]);

  const visible = filtered.slice(0, page * PAGE_SIZE);
  const canLoadMore = visible.length < filtered.length;

  function resetPage(setter, value) {
    setPage(1);
    setter(value);
  }

  function clearFilters() {
    setQuery('');
    setCategory('');
    setCondition('');
    setRoutine('');
    setPriceRange('');
    setSort('name-asc');
    setPage(1);
  }

  const hasFilters = Boolean(query || category || condition || routine || priceRange || sort !== 'name-asc');

  return (
    <ErrorBoundary>
      <main className="page page--catalog">
        <section className="catalog-hero">
          <div>
            <p className="eyebrow">Nyafaz demo catalog</p>
            <h1>Shop by concern</h1>
            <p className="muted">
              Cleanse, treat and moisturize with demo products mapped to skin conditions for recommendation testing.
            </p>
          </div>
          <div className="catalog-stats">
            <div>
              <strong>{products.length}</strong>
              <span>Products</span>
            </div>
            <div>
              <strong>{categories.length}</strong>
              <span>Categories</span>
            </div>
            <div>
              <strong>{conditions.length}</strong>
              <span>Concerns</span>
            </div>
          </div>
        </section>

        <section className="toolbar catalog-toolbar">
          <div className="catalog-search">
            <input
              className="input"
              placeholder="Search products, ingredients or concerns"
              value={query}
              onChange={(event) => resetPage(setQuery, event.target.value)}
            />
          </div>
          <div className="filters">
            <select className="select" value={category} onChange={(event) => resetPage(setCategory, event.target.value)}>
              <option value="">Category</option>
              {categories.map((item) => (
                <option key={item}>{item}</option>
              ))}
            </select>
            <select className="select" value={condition} onChange={(event) => resetPage(setCondition, event.target.value)}>
              <option value="">Concern</option>
              {conditions.map((item) => (
                <option key={item}>{item}</option>
              ))}
            </select>
            <select className="select" value={priceRange} onChange={(event) => resetPage(setPriceRange, event.target.value)}>
              <option value="">Price</option>
              <option value="0-899">Under ₹900</option>
              <option value="900-1099">₹900 – ₹1099</option>
              <option value="1100-99999">₹1100+</option>
            </select>
            <select className="select" value={routine} onChange={(event) => resetPage(setRoutine, event.target.value)}>
              <option value="">Routine</option>
              {routines.map((item) => (
                <option key={item}>{item}</option>
              ))}
            </select>
            <select className="select" value={sort} onChange={(event) => setSort(event.target.value)}>
              <option value="name-asc">Name A–Z</option>
              <option value="name-desc">Name Z–A</option>
              <option value="price-asc">Price ↑</option>
              <option value="price-desc">Price ↓</option>
            </select>
          </div>
        </section>

        <div className="catalog-results-bar">
          <p>
            Showing <strong>{visible.length}</strong> of <strong>{filtered.length}</strong> products
          </p>
          {hasFilters ? (
            <button className="btn ghost" type="button" onClick={clearFilters}>
              Clear filters
            </button>
          ) : null}
        </div>

        {!products.length && <div className="skeleton">Loading products...</div>}
        {products.length > 0 && !filtered.length && <div className="empty">No products match your filters.</div>}

        <section className="grid catalog-grid">
          {visible.map((product) => (
            <ProductCard product={product} key={product.slug} />
          ))}
        </section>

        {canLoadMore && (
          <div className="load-more">
            <button className="btn secondary" type="button" onClick={() => setPage((value) => value + 1)}>
              Load more products
            </button>
          </div>
        )}
      </main>
    </ErrorBoundary>
  );
}
